import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { login as authLogin, logout as authLogout, getCurrentUser, isAuthenticated, refreshToken } from '../services/auth';

interface User {
    id: string;
    username: string;
    email?: string;
    role?: string;
    mustChangePassword?: boolean;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
    logout: () => void;
    checkAuth: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const checkAuth = useCallback((): boolean => {
        try {
            if (isAuthenticated()) {
                const userData = getCurrentUser();
                setUser(userData);
                setIsLoading(false);
                // Sliding session: extend the token by ~7d on every app open.
                // Fire-and-forget — the current token is still valid, so the UI
                // doesn't wait on this.
                void refreshToken();
                return true;
            } else {
                setUser(null);
                setIsLoading(false);
                return false;
            }
        } catch (error) {
            console.error('Error checking authentication:', error);
            logout();
            return false;
        }
    }, []);

    // Check if user is authenticated on app load
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const { user: userData } = await authLogin(username, password);
            setUser(userData);
            setIsLoading(false);
            return { success: true };
        } catch (error: any) {
            console.error('Login error:', error);
            return {
                success: false,
                error: error?.message || 'Network error. Please try again.',
            };
        }
    };

    const logout = () => {
        authLogout();
        setUser(null);
        setIsLoading(false);
    };

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        checkAuth,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 